
import React, { useState, useRef } from 'react';
import { Company, CardConfig, CardFieldDef } from '../types';
import { api } from '../services/apiService';

interface CardConfigTabProps {
  company: Company;
  onCompanyUpdate: (updated: Company) => void;
}

const DEFAULT_FIELDS: CardFieldDef[] = [
  { key: 'name',              enabled: true  },
  { key: 'email',             enabled: true  },
  { key: 'phone',             enabled: false },
  { key: 'clientCompanyName', enabled: true  },
  { key: 'status',            enabled: true  },
  { key: 'potentialRevenue',  enabled: true  },
  { key: 'sectorActivity',    enabled: true  },
  { key: 'tag',               enabled: false },
  { key: 'createdAt',         enabled: true  },
  { key: 'agent',             enabled: true  },
];

const FIELD_LABELS: Record<CardFieldDef['key'], string> = {
  name:              'Nom du prospect',
  email:             'Email',
  phone:             'Téléphone',
  clientCompanyName: 'Entreprise cliente',
  status:            'Statut',
  potentialRevenue:  'Revenu potentiel',
  sectorActivity:    'Secteur d\'activité',
  tag:               'Tag / Étiquette',
  createdAt:         'Date de création',
  agent:             'Agent assigné',
};

const FIELD_ICONS: Record<CardFieldDef['key'], React.ReactNode> = {
  name:              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>,
  email:             <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>,
  phone:             <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>,
  clientCompanyName: <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>,
  status:            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
  potentialRevenue:  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
  sectorActivity:    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" /></svg>,
  tag:               <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" /></svg>,
  createdAt:         <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>,
  agent:             <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
};

function getEffectiveConfig(company: Company): CardConfig {
  if (company.cardConfig?.fields?.length) return company.cardConfig;
  return { fields: DEFAULT_FIELDS, showQuickCall: false };
}

const CardConfigTab: React.FC<CardConfigTabProps> = ({ company, onCompanyUpdate }) => {
  const [config, setConfig] = useState<CardConfig>(() => getEffectiveConfig(company));
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Drag & drop state
  const dragIndex = useRef<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);

  const save = async (newConfig: CardConfig) => {
    setSaving(true);
    setMessage(null);
    try {
      const updated = await api.updateCompany(company.id, { cardConfig: newConfig } as any);
      onCompanyUpdate(updated);
      setMessage({ type: 'success', text: 'Sauvegardé.' });
      setTimeout(() => setMessage(null), 2000);
    } catch {
      setMessage({ type: 'error', text: 'Erreur lors de la sauvegarde.' });
    } finally {
      setSaving(false);
    }
  };

  const toggle = (key: CardFieldDef['key']) => {
    if (key === 'name') return; // le nom est toujours visible
    const newFields = config.fields.map(f => f.key === key ? { ...f, enabled: !f.enabled } : f);
    const newConfig = { ...config, fields: newFields };
    setConfig(newConfig);
    save(newConfig);
  };

  const toggleQuickCall = () => {
    const newConfig = { ...config, showQuickCall: !config.showQuickCall };
    setConfig(newConfig);
    save(newConfig);
  };

  // Drag & drop handlers
  const onDragStart = (index: number) => { dragIndex.current = index; };
  const onDragOver = (e: React.DragEvent, index: number) => { e.preventDefault(); setDragOver(index); };
  const onDrop = (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    const from = dragIndex.current;
    if (from === null || from === targetIndex) { setDragOver(null); dragIndex.current = null; return; }
    const newFields = [...config.fields];
    const [moved] = newFields.splice(from, 1);
    newFields.splice(targetIndex, 0, moved);
    const newConfig = { ...config, fields: newFields };
    setConfig(newConfig);
    save(newConfig);
    dragIndex.current = null;
    setDragOver(null);
  };
  const onDragEnd = () => { dragIndex.current = null; setDragOver(null); };

  // Données factices pour la prévisualisation
  const previewLead = {
    name: 'Marie Dupont',
    email: 'ma•••@entreprise.fr',
    phone: '06 •• •• •• 42',
    clientCompanyName: 'Acme Corp',
    status: 'Nouveau',
    statusColor: '#6366f1',
    potentialRevenue: '+100K',
    revenueColor: '#10b981',
    sectorActivity: 'Technologie',
    tag: '#crm',
    createdAt: '12 mars',
    agent: 'Laurent A.',
    agentInitial: 'L',
  };

  const enabledFields = config.fields.filter(f => f.enabled);

  return (
    <div className="p-12 animate-in fade-in">
      <h3 className="text-2xl font-extrabold text-slate-900 mb-1 tracking-tight">Configuration des cartes</h3>
      <p className="text-slate-500 text-sm font-medium mb-2">
        Choisissez les informations affichées sur chaque carte prospect, leur ordre, et les actions rapides disponibles.
      </p>
      <div className="flex items-center gap-2 mb-8">
        <span className="text-[10px] font-black uppercase tracking-widest bg-[rgba(100,140,210,0.15)] text-[#546a96] border border-[rgba(100,140,210,0.25)] px-2.5 py-1 rounded-lg">
          Vue Cartes &amp; Kanban
        </span>
        {message && (
          <span className={`text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-lg ${
            message.type === 'success'
              ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
              : 'bg-rose-500/20 text-rose-300 border border-rose-500/30'
          }`}>{message.text}</span>
        )}
      </div>

      <div className="flex flex-col xl:flex-row gap-8">

        {/* ── Colonne gauche : configurateur ── */}
        <div className="flex-1 min-w-0 space-y-5">

          {/* Champs draggables */}
          <div className="orion-settings-pref-card rounded-2xl p-6">
            <div className="text-[10px] font-black uppercase tracking-widest text-[#546a96] mb-1">Champs affichés</div>
            <p className="text-[11px] text-[#7f93bc] mb-4">Glissez-déposez pour réordonner. Activez/désactivez chaque champ.</p>
            <div className="space-y-1.5">
              {config.fields.map((field, index) => (
                <div
                  key={field.key}
                  draggable
                  onDragStart={() => onDragStart(index)}
                  onDragOver={(e) => onDragOver(e, index)}
                  onDrop={(e) => onDrop(e, index)}
                  onDragEnd={onDragEnd}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all cursor-grab active:cursor-grabbing ${
                    dragOver === index
                      ? 'border-[rgba(17,197,214,0.5)] bg-[rgba(17,197,214,0.06)]'
                      : 'border-[rgba(100,140,210,0.12)] bg-[rgba(100,140,210,0.04)] hover:bg-[rgba(100,140,210,0.08)]'
                  } ${saving ? 'opacity-60 pointer-events-none' : ''}`}
                >
                  {/* Icône drag */}
                  <svg className="w-3.5 h-3.5 text-[#3d5280] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <circle cx="6" cy="4" r="1.5"/><circle cx="14" cy="4" r="1.5"/>
                    <circle cx="6" cy="10" r="1.5"/><circle cx="14" cy="10" r="1.5"/>
                    <circle cx="6" cy="16" r="1.5"/><circle cx="14" cy="16" r="1.5"/>
                  </svg>

                  {/* Icône champ */}
                  <span className="text-[#546a96] flex-shrink-0">{FIELD_ICONS[field.key]}</span>

                  {/* Label */}
                  <span className={`flex-1 text-sm font-bold ${field.enabled ? 'text-slate-900' : 'text-[#546a96]'}`}>
                    {FIELD_LABELS[field.key]}
                  </span>

                  {/* Badge "Obligatoire" pour le nom */}
                  {field.key === 'name' && (
                    <span className="text-[9px] font-black uppercase tracking-widest bg-[rgba(100,140,210,0.1)] text-[#546a96] px-2 py-0.5 rounded-md">Toujours visible</span>
                  )}

                  {/* Toggle */}
                  {field.key !== 'name' && (
                    <button
                      type="button"
                      disabled={saving}
                      onClick={() => toggle(field.key)}
                      className={`relative flex-shrink-0 w-10 h-5 rounded-full transition-all duration-200 focus:outline-none ${
                        field.enabled
                          ? 'bg-[rgba(17,197,214,0.8)]'
                          : 'bg-[rgba(100,140,210,0.2)]'
                      } disabled:opacity-50`}
                      aria-label={`${field.enabled ? 'Masquer' : 'Afficher'} ${FIELD_LABELS[field.key]}`}
                    >
                      <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform duration-200 ${field.enabled ? 'translate-x-5' : 'translate-x-0'}`} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Bouton appel rapide */}
          <div className="orion-settings-pref-card rounded-2xl p-6">
            <div className="text-[10px] font-black uppercase tracking-widest text-[#546a96] mb-4">Actions rapides</div>
            <div className="flex items-center justify-between gap-4 py-1">
              <div>
                <div className="flex items-center gap-2 mb-0.5">
                  <svg className="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                  <span className="text-sm font-bold text-slate-900">Bouton appel rapide</span>
                </div>
                <p className="text-[11px] text-slate-500">Affiche un bouton sur chaque carte pour appeler directement le prospect (lien <code className="text-[10px] bg-slate-100 px-1 rounded">tel:</code>).</p>
              </div>
              <button
                type="button"
                disabled={saving}
                onClick={toggleQuickCall}
                className={`relative flex-shrink-0 w-11 h-6 rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                  config.showQuickCall
                    ? 'bg-emerald-500 focus:ring-emerald-400'
                    : 'bg-[rgba(100,140,210,0.2)] focus:ring-[rgba(100,140,210,0.4)]'
                } disabled:opacity-50`}
                aria-label={config.showQuickCall ? 'Désactiver appel rapide' : 'Activer appel rapide'}
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-200 ${config.showQuickCall ? 'translate-x-5' : 'translate-x-0'}`} />
              </button>
            </div>
          </div>
        </div>

        {/* ── Colonne droite : prévisualisation ── */}
        <div className="xl:w-72 flex-shrink-0">
          <div className="sticky top-6">
            <div className="text-[10px] font-black uppercase tracking-widest text-[#546a96] mb-3">Prévisualisation</div>
            <div
              className="orion-lead-card p-4 rounded-xl border border-[rgba(100,140,210,0.2)] shadow-sm relative"
              style={{ background: 'var(--card-bg, rgba(14,28,58,0.95))' }}
            >
              {/* Bouton appel rapide */}
              {config.showQuickCall && (
                <a
                  href="tel:+33600000000"
                  onClick={e => e.preventDefault()}
                  className="absolute top-3 right-3 w-7 h-7 rounded-lg bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center text-emerald-400 hover:bg-emerald-500/30 transition-all z-10"
                  title="Appeler"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                </a>
              )}

              {/* En-tête : avatar + nom/email */}
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center text-xs font-black text-white shadow-sm flex-shrink-0" style={{ backgroundColor: '#6366f1' }}>
                  M
                </div>
                <div className="leading-tight min-w-0 flex-1">
                  {enabledFields.some(f => f.key === 'name') && (
                    <div className="text-sm font-bold text-[#e0eaff] truncate">{previewLead.name}</div>
                  )}
                  {enabledFields.some(f => f.key === 'email') && (
                    <div className="text-[10px] text-[#546a96] truncate">{previewLead.email}</div>
                  )}
                  {enabledFields.some(f => f.key === 'phone') && (
                    <div className="text-[10px] text-[#546a96] truncate">{previewLead.phone}</div>
                  )}
                </div>
              </div>

              {/* Entreprise */}
              {enabledFields.some(f => f.key === 'clientCompanyName') && (
                <div className="text-[10px] font-bold text-[#7f93bc] mb-2 truncate">
                  🏢 {previewLead.clientCompanyName}
                </div>
              )}

              {/* Secteur */}
              {enabledFields.some(f => f.key === 'sectorActivity') && (
                <div className="mb-2">
                  <span className="text-[9px] font-bold bg-[rgba(100,140,210,0.12)] px-2 py-0.5 rounded-md text-[#7f93bc]">{previewLead.sectorActivity}</span>
                </div>
              )}

              {/* Statut */}
              {enabledFields.some(f => f.key === 'status') && (
                <div className="mb-2">
                  <span className="text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-md" style={{ backgroundColor: previewLead.statusColor + '22', color: previewLead.statusColor, border: `1px solid ${previewLead.statusColor}44` }}>
                    {previewLead.status}
                  </span>
                </div>
              )}

              {/* Tag */}
              {enabledFields.some(f => f.key === 'tag') && (
                <div className="mb-2">
                  <span className="text-[9px] font-bold text-[#11c5d6] bg-[rgba(17,197,214,0.1)] px-2 py-0.5 rounded-md">{previewLead.tag}</span>
                </div>
              )}

              {/* Footer */}
              {(enabledFields.some(f => f.key === 'agent') || enabledFields.some(f => f.key === 'potentialRevenue') || enabledFields.some(f => f.key === 'createdAt')) && (
                <div className="flex items-center justify-between pt-2.5 border-t border-[rgba(100,140,210,0.12)] mt-1">
                  <div className="flex items-center gap-1.5">
                    {enabledFields.some(f => f.key === 'agent') && (
                      <>
                        <div className="w-5 h-5 rounded-full bg-[rgba(100,140,210,0.2)] border border-[rgba(100,140,210,0.3)] flex items-center justify-center text-[8px] font-black text-[#7f93bc]">
                          {previewLead.agentInitial}
                        </div>
                        <span className="text-[9px] font-bold text-[#7f93bc] truncate max-w-[60px]">{previewLead.agent}</span>
                      </>
                    )}
                  </div>
                  <div className="text-right">
                    {enabledFields.some(f => f.key === 'potentialRevenue') && (
                      <div className="flex items-center justify-end gap-1">
                        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: previewLead.revenueColor }} />
                        <span className="text-[10px] font-black" style={{ color: previewLead.revenueColor }}>{previewLead.potentialRevenue}</span>
                      </div>
                    )}
                    {enabledFields.some(f => f.key === 'createdAt') && (
                      <div className="text-[8px] font-bold text-[#3d5280]">{previewLead.createdAt}</div>
                    )}
                  </div>
                </div>
              )}

              {enabledFields.length === 0 && (
                <div className="text-[10px] text-[#3d5280] italic text-center py-2">Aucun champ activé</div>
              )}
            </div>

            <p className="text-[10px] text-[#3d5280] mt-3 leading-relaxed">
              La prévisualisation reflète exactement l'apparence dans la vue <span className="font-bold">Cartes</span> et <span className="font-bold">Kanban</span>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export { DEFAULT_FIELDS, getEffectiveConfig };
export type { CardConfig };
export default CardConfigTab;
